import {
  getAdminContentEditUrl,
  getStationViewPath,
} from "./videoStationAdminRoutes.mjs";

export const ADMIN_DRAFT_QUEUE_QUERY_KEY = "draftQueue";
export const ADMIN_DRAFT_QUEUE_STORAGE_PREFIX = "admin-draft-queue";
export const ADMIN_DRAFT_QUEUE_KIND_CONTENT = "content";
export const ADMIN_DRAFT_QUEUE_KIND_FLASHCARD = "flashcard";
export const ADMIN_DRAFT_QUEUE_DESTINATION_EDITOR = "editor";
export const ADMIN_DRAFT_QUEUE_DESTINATION_PREVIEW = "preview";
export const ADMIN_DRAFT_QUEUE_DEFAULT_LABEL =
  "All drafts - Last updated, newest first";
export const ADMIN_DRAFT_QUEUE_FILTERED_COMPLETE_NOTICE =
  "Filtered queue complete. Continuing with remaining drafts by Last updated, newest first.";

const QUEUE_CONTEXT_VERSION = 1;
const DEFAULT_QUEUE_SORT = [{ key: "lastUpdated", order: "desc" }];
const SUPPORTED_SORT_KEYS = new Set([
  "id",
  "created",
  "createdBy",
  "title",
  "text",
  "textPreview",
  "category",
  "explanationRefTitle",
  "qaSetRefTitle",
  "number",
  "stats",
  "statusLabel",
  "type",
  "questionType",
  "userRating",
  "commentCount",
  "approved",
  "approvedBy",
  "updated",
  "lastUpdated",
]);

export const normalizeQueueId = (value) => String(value ?? "").trim();

const normalizeAdminDraftQueueBankHints = (bankHintsById = {}) => {
  if (!bankHintsById || typeof bankHintsById !== "object") return {};

  return Object.fromEntries(
    Object.entries(bankHintsById)
      .map(([id, bank]) => [normalizeQueueId(id), normalizeQueueId(bank)])
      .filter(([id, bank]) => id && bank),
  );
};

export const createAdminDraftQueueKey = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export const getAdminDraftQueueStorageKey = (key) =>
  `${ADMIN_DRAFT_QUEUE_STORAGE_PREFIX}:${normalizeQueueId(key)}`;

const getBrowserSessionStorage = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const getNestedValue = (source, path) =>
  path
    .split(".")
    .reduce((value, key) => (value == null ? undefined : value[key]), source);

const getFirstValue = (row, paths) => {
  for (const path of paths) {
    const value = getNestedValue(row, path);
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }

    const rawValue = getNestedValue(row?.raw, path);
    if (rawValue !== undefined && rawValue !== null && rawValue !== "") {
      return rawValue;
    }
  }

  return undefined;
};

const getTimestamp = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isFinite(time) ? time : 0;
  }

  if (value && typeof value.toMillis === "function") {
    return value.toMillis();
  }

  if (value && typeof value.seconds === "number") {
    return value.seconds * 1000;
  }

  if (value && typeof value._seconds === "number") {
    return value._seconds * 1000;
  }

  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  return 0;
};

const getTimestampValue = (row, paths) =>
  getTimestamp(getFirstValue(row, paths));

const getText = (value) => normalizeQueueId(value).toLowerCase();

const getNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const getOptionalNumber = (value) => {
  if (value === undefined || value === null || value === "") {
    return undefined;
  }

  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
};

const getUserRatingSortValue = (row) => {
  const directScore = getFirstValue(row, ["userRatingScore"]);
  const directNumber = Number(directScore);
  if (Number.isFinite(directNumber)) {
    return directNumber;
  }

  const rating = getFirstValue(row, ["userRating"]) || {};
  const up = getNumber(rating.up);
  const down = getNumber(rating.down);
  const total = up + down;
  if (total <= 0) {
    return -1;
  }

  return (up / total) * 100 + total / 1000;
};

const getSortValue = (row, sortKey) => {
  switch (sortKey) {
    case "id":
      return getText(getFirstValue(row, ["id", "entityId", "flashcardId"]));
    case "created":
    case "createdBy":
      return getTimestampValue(row, [
        "createdAtMs",
        "createdAt",
        "createdBy.timestamp",
      ]);
    case "title":
      return getText(getFirstValue(row, ["title"]));
    case "text":
    case "textPreview":
      return getText(getFirstValue(row, ["text", "textPreview", "preview"]));
    case "category":
      return getText(getFirstValue(row, ["category", "subtitle"]));
    case "explanationRefTitle":
      return getText(getFirstValue(row, ["explanationRefTitle"]));
    case "qaSetRefTitle":
      return getText(getFirstValue(row, ["qaSetRefTitle"]));
    case "number":
      return getNumber(getFirstValue(row, ["number"]));
    case "stats":
      return getNumber(
        getFirstValue(row, [
          "statsAccuracyPct",
          "stats.accuracyPct",
          "stats.accuracy",
        ]),
      );
    case "statusLabel":
      return getText(
        getFirstValue(row, ["statusLabel", "draftTypeLabel", "draftType"]),
      );
    case "type":
    case "questionType":
      return getText(getFirstValue(row, ["type", "questionType", "subtitle"]));
    case "userRating":
      return getUserRatingSortValue(row);
    case "commentCount":
      return getNumber(
        getFirstValue(row, ["unresolvedCommentCount", "commentCount"]),
      );
    case "approved":
    case "approvedBy":
      return getTimestampValue(row, [
        "approvedAtMs",
        "approvedAt",
        "approvedBy.timestamp",
      ]);
    case "updated":
    case "lastUpdated":
    default:
      return getTimestampValue(row, [
        "updatedAtMs",
        "updatedAt",
        "lastUpdated",
      ]);
  }
};

export const normalizeAdminDraftQueueSort = (sortBy = DEFAULT_QUEUE_SORT) => {
  const primary = Array.isArray(sortBy) ? sortBy[0] : sortBy;
  const requestedKey = normalizeQueueId(primary?.key);
  if (!SUPPORTED_SORT_KEYS.has(requestedKey)) {
    return { ...DEFAULT_QUEUE_SORT[0] };
  }

  const key = requestedKey;
  const order =
    normalizeQueueId(primary?.order).toLowerCase() === "asc" ? "asc" : "desc";

  return { key, order };
};

export const sortAdminDraftRows = ({
  rows = [],
  sortBy = DEFAULT_QUEUE_SORT,
} = {}) => {
  const { key, order } = normalizeAdminDraftQueueSort(sortBy);
  const multiplier = order === "asc" ? 1 : -1;

  return [...rows].sort((left, right) => {
    const leftValue = getSortValue(left, key);
    const rightValue = getSortValue(right, key);
    let result = 0;

    if (typeof leftValue === "number" || typeof rightValue === "number") {
      result = (Number(leftValue) || 0) - (Number(rightValue) || 0);
    } else {
      result = String(leftValue).localeCompare(String(rightValue));
    }

    if (result !== 0) {
      return result * multiplier;
    }

    return normalizeQueueId(
      getFirstValue(left, ["id", "entityId", "flashcardId"]),
    ).localeCompare(
      normalizeQueueId(getFirstValue(right, ["id", "entityId", "flashcardId"])),
    );
  });
};

const getTableSortHeader = (headers = [], sortKey = "") =>
  headers.find(
    (header) => normalizeQueueId(header?.key) === normalizeQueueId(sortKey),
  );

const getTableSortFieldValue = (row, sortKey) => {
  const key = normalizeQueueId(sortKey);
  if (!key) return undefined;

  const value = getNestedValue(row, key);
  if (value !== undefined) return value;

  return getNestedValue(row?.raw, key);
};

const isEmptyTableSortValue = (value) =>
  value === undefined ||
  value === null ||
  (typeof value === "string" && value.trim() === "");

const getDateStringSortTime = (value) => {
  const text = String(value || "").trim();
  if (!/[/:T-]/.test(text)) return null;

  const parsed = Date.parse(text);
  return Number.isNaN(parsed) ? null : parsed;
};

const getDefaultTableSortValue = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return { type: "number", value };
  }

  if (
    value instanceof Date ||
    (value && typeof value.toMillis === "function") ||
    (value && typeof value.seconds === "number") ||
    (value && typeof value._seconds === "number")
  ) {
    return { type: "number", value: getTimestamp(value) };
  }

  if (typeof value === "string") {
    const numericValue = Number(value);
    if (/^-?\d+(\.\d+)?$/.test(value.trim()) && Number.isFinite(numericValue)) {
      return { type: "number", value: numericValue };
    }

    const dateValue = getDateStringSortTime(value);
    if (dateValue !== null) {
      return { type: "number", value: dateValue };
    }
  }

  return { type: "string", value: normalizeQueueId(value).toLowerCase() };
};

const compareDefaultTableSortValues = (leftValue, rightValue) => {
  const leftEmpty = isEmptyTableSortValue(leftValue);
  const rightEmpty = isEmptyTableSortValue(rightValue);
  if (leftEmpty || rightEmpty) {
    if (leftEmpty && rightEmpty) return 0;
    return leftEmpty ? -1 : 1;
  }

  const left = getDefaultTableSortValue(leftValue);
  const right = getDefaultTableSortValue(rightValue);
  if (left.type === "number" && right.type === "number") {
    return left.value - right.value;
  }

  return String(left.value).localeCompare(String(right.value));
};

const compareDefaultTableSortRows = (leftRow, rightRow, sortKey) =>
  compareDefaultTableSortValues(
    getTableSortFieldValue(leftRow, sortKey),
    getTableSortFieldValue(rightRow, sortKey),
  );

export const sortAdminDraftRowsForTableLaunch = ({
  rows = [],
  headers = [],
  sortBy = [],
} = {}) => {
  const primarySort = Array.isArray(sortBy) ? sortBy[0] : sortBy;
  const sortKey = normalizeQueueId(primarySort?.key);
  if (!sortKey) {
    return [...rows];
  }

  const order =
    normalizeQueueId(primarySort?.order).toLowerCase() === "desc"
      ? "desc"
      : "asc";
  const header = getTableSortHeader(headers, sortKey);
  const compareRows = (leftItem, rightItem) => {
    const leftRow = order === "desc" ? rightItem.row : leftItem.row;
    const rightRow = order === "desc" ? leftItem.row : rightItem.row;
    const result =
      typeof header?.sortRaw === "function"
        ? header.sortRaw(leftRow, rightRow)
        : compareDefaultTableSortRows(leftRow, rightRow, sortKey);
    const numericResult = Number(result);

    if (Number.isFinite(numericResult) && numericResult !== 0) {
      return numericResult;
    }

    return leftItem.index - rightItem.index;
  };

  return rows
    .map((row, index) => ({ row, index }))
    .sort(compareRows)
    .map((item) => item.row);
};

export const normalizeAdminDraftQueueRow = (
  row = {},
  queuePhase = "default",
) => {
  const id = normalizeQueueId(
    getFirstValue(row, ["id", "entityId", "flashcardId"]),
  );
  const category = normalizeQueueId(row.category);
  const type = normalizeQueueId(row.type);
  const questionType = normalizeQueueId(row.questionType);
  const textPreview = normalizeQueueId(
    row.textPreview || row.text || row.title,
  );
  const statusLabel = normalizeQueueId(row.statusLabel);
  const draftTypeLabel = normalizeQueueId(
    row.draftTypeLabel || row.statusLabel || row.draftType,
  );

  return {
    id,
    bankId: normalizeQueueId(getFirstValue(row, ["bankId"])),
    title: normalizeQueueId(row.title),
    subtitle: category || type || questionType,
    preview: textPreview,
    draftTypeLabel,
    category,
    type,
    questionType,
    text: normalizeQueueId(row.text),
    textPreview,
    statusLabel,
    createdAtMs: getTimestampValue(row, [
      "createdAtMs",
      "createdAt",
      "createdBy.timestamp",
    ]),
    updatedAtMs: getTimestampValue(row, [
      "updatedAtMs",
      "updatedAt",
      "lastUpdated",
    ]),
    createdByName: normalizeQueueId(row.createdByName || row.createdBy?.name),
    updatedByName: normalizeQueueId(
      row.updatedByName || row.updatedBy?.name || row.lastUpdatedBy,
    ),
    userRating: row.userRating || null,
    userRatingScore: getFirstValue(row, ["userRatingScore"]),
    commentCount: getOptionalNumber(row.commentCount) ?? 0,
    unresolvedCommentCount: getOptionalNumber(row.unresolvedCommentCount),
    queuePhase,
    raw: row.raw || row,
  };
};

export const buildAdminDraftQueueRows = ({
  rows = [],
  orderedIds = [],
  sortBy = DEFAULT_QUEUE_SORT,
  strictOrderedIds = false,
} = {}) => {
  const normalizedRows = rows
    .map((row) => normalizeAdminDraftQueueRow(row))
    .filter((row) => row.id);
  const rowById = new Map(normalizedRows.map((row) => [row.id, row]));
  const used = new Set();
  const filteredRows = [];

  for (const orderedId of orderedIds) {
    const id = normalizeQueueId(orderedId);
    if (!id || used.has(id) || !rowById.has(id)) {
      continue;
    }

    used.add(id);
    filteredRows.push({ ...rowById.get(id), queuePhase: "filtered" });
  }

  if (strictOrderedIds) {
    return filteredRows;
  }

  const defaultRows = sortAdminDraftRows({
    rows: normalizedRows.filter((row) => !used.has(row.id)),
    sortBy,
  }).map((row) => ({ ...row, queuePhase: "default" }));

  return [...filteredRows, ...defaultRows];
};

export const buildAdminDraftQueueRowsFromOrderedIds = (
  orderedIds = [],
  { bankHintsById = {} } = {},
) => {
  if (!Array.isArray(orderedIds)) return [];

  const used = new Set();
  const normalizedBankHints = normalizeAdminDraftQueueBankHints(bankHintsById);
  return orderedIds
    .map(normalizeQueueId)
    .filter((id) => {
      if (!id || used.has(id)) return false;
      used.add(id);
      return true;
    })
    .map((id) =>
      normalizeAdminDraftQueueRow(
        {
          id,
          bankId: normalizedBankHints[id],
        },
        "filtered",
      ),
    );
};

export const getAdminDraftQueuePosition = (rows = [], currentId = "") => {
  const normalizedCurrentId = normalizeQueueId(currentId);
  return rows.findIndex(
    (row) => normalizeQueueId(row?.id) === normalizedCurrentId,
  );
};

export const getNextAdminDraftQueueStateAfterRemoval = ({
  rows = [],
  currentId = "",
  forwardOnly = false,
} = {}) => {
  const normalizedCurrentId = normalizeQueueId(currentId);
  const currentIndex = getAdminDraftQueuePosition(rows, normalizedCurrentId);
  const currentRow = currentIndex >= 0 ? rows[currentIndex] : null;
  const remaining = rows.filter(
    (row) => normalizeQueueId(row?.id) !== normalizedCurrentId,
  );
  const nextRow =
    currentIndex < 0
      ? remaining[0] || null
      : remaining[currentIndex] ||
        (forwardOnly ? null : remaining[currentIndex - 1] || remaining[0]) ||
        null;
  const phaseChanged =
    currentRow?.queuePhase === "filtered" && nextRow?.queuePhase === "default";

  return {
    rows: remaining,
    nextId: nextRow?.id || "",
    phaseChanged,
    notice: phaseChanged ? ADMIN_DRAFT_QUEUE_FILTERED_COMPLETE_NOTICE : "",
  };
};

export const canAdvanceAdminDraftQueueAfterRemoval = ({
  rows = [],
  currentId = "",
  forwardOnly = false,
} = {}) =>
  Boolean(
    getNextAdminDraftQueueStateAfterRemoval({
      rows,
      currentId,
      forwardOnly,
    }).nextId,
  );

export const resolveAdminDraftQueueExitPath = ({
  context = null,
  fallbackPath = "",
} = {}) =>
  normalizeQueueId(context?.sourcePath) || normalizeQueueId(fallbackPath);

export const buildAdminDraftQueueContext = ({
  kind = ADMIN_DRAFT_QUEUE_KIND_CONTENT,
  bankId = "",
  selectedBanks = [],
  sourcePath = "",
  label = ADMIN_DRAFT_QUEUE_DEFAULT_LABEL,
  orderedIds = [],
  bankHintsById = {},
} = {}) => ({
  version: QUEUE_CONTEXT_VERSION,
  kind: normalizeQueueId(kind) || ADMIN_DRAFT_QUEUE_KIND_CONTENT,
  bankId: normalizeQueueId(bankId),
  selectedBanks: Array.isArray(selectedBanks)
    ? selectedBanks.map(normalizeQueueId).filter(Boolean)
    : [],
  sourcePath: normalizeQueueId(sourcePath),
  label: normalizeQueueId(label) || ADMIN_DRAFT_QUEUE_DEFAULT_LABEL,
  orderedIds: Array.isArray(orderedIds)
    ? orderedIds.map(normalizeQueueId).filter(Boolean)
    : [],
  bankHintsById: normalizeAdminDraftQueueBankHints(bankHintsById),
  createdAt: Date.now(),
});

export const getAdminDraftQueueBankHint = ({
  context = null,
  id = "",
  fallbackBank = "",
} = {}) => {
  const normalizedId = normalizeQueueId(id);
  return (
    normalizeQueueId(context?.bankHintsById?.[normalizedId]) ||
    normalizeQueueId(fallbackBank)
  );
};

export const updateAdminDraftQueueContextOrderedIds = ({
  context = null,
  orderedIds = [],
} = {}) => {
  if (!context || context.version !== QUEUE_CONTEXT_VERSION) {
    return context;
  }

  const normalizedOrderedIds = Array.isArray(orderedIds)
    ? orderedIds.map(normalizeQueueId).filter(Boolean)
    : [];
  const remainingIds = new Set(normalizedOrderedIds);
  const bankHintsById = Object.fromEntries(
    Object.entries(
      normalizeAdminDraftQueueBankHints(context.bankHintsById),
    ).filter(([id]) => remainingIds.has(id)),
  );

  return {
    ...context,
    orderedIds: normalizedOrderedIds,
    ...(context.bankHintsById ? { bankHintsById } : {}),
  };
};

export const saveAdminDraftQueueContext = ({
  key,
  context,
  storage = getBrowserSessionStorage(),
} = {}) => {
  if (!storage || !normalizeQueueId(key)) {
    return false;
  }

  try {
    storage.setItem(getAdminDraftQueueStorageKey(key), JSON.stringify(context));
    return true;
  } catch {
    return false;
  }
};

export const loadAdminDraftQueueContext = ({
  key,
  storage = getBrowserSessionStorage(),
} = {}) => {
  if (!storage || !normalizeQueueId(key)) {
    return null;
  }

  try {
    const parsed = JSON.parse(
      storage.getItem(getAdminDraftQueueStorageKey(key)) || "null",
    );
    if (!parsed || parsed.version !== QUEUE_CONTEXT_VERSION) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

export const buildAdminDraftQueueLabel = ({
  search = "",
  filters = [],
  sortLabel = "",
} = {}) => {
  const parts = [];
  const cleanSearch = normalizeQueueId(search);
  if (cleanSearch) {
    parts.push(`Search "${cleanSearch}"`);
  }

  if (Array.isArray(filters)) {
    parts.push(...filters.map(normalizeQueueId).filter(Boolean));
  }

  const cleanSortLabel = normalizeQueueId(sortLabel);
  if (cleanSortLabel) {
    parts.push(cleanSortLabel);
  }

  return parts.length ? parts.join(" - ") : ADMIN_DRAFT_QUEUE_DEFAULT_LABEL;
};

export const buildAdminDraftQueueRoute = ({
  kind,
  bankId,
  id,
  queueKey = "",
  contentEditor = "default",
  destination = ADMIN_DRAFT_QUEUE_DESTINATION_EDITOR,
  bankHint = "",
} = {}) => {
  const normalizedQueueKey = normalizeQueueId(queueKey);
  const query = normalizedQueueKey
    ? { [ADMIN_DRAFT_QUEUE_QUERY_KEY]: normalizedQueueKey }
    : {};

  const isPreview =
    normalizeQueueId(destination) === ADMIN_DRAFT_QUEUE_DESTINATION_PREVIEW;

  if (kind === ADMIN_DRAFT_QUEUE_KIND_FLASHCARD && isPreview) {
    const normalizedBankHint = normalizeQueueId(bankHint);
    return {
      name: "FlashCardView",
      params: { id: normalizeQueueId(id) },
      query: {
        draft: "true",
        ...query,
        ...(normalizedBankHint ? { bank: normalizedBankHint } : {}),
      },
    };
  }

  if (kind === ADMIN_DRAFT_QUEUE_KIND_FLASHCARD) {
    return {
      name: "AdminFlashcardVisualEdit",
      params: { id: normalizeQueueId(id) },
      query,
    };
  }

  if (isPreview) {
    return {
      path: getStationViewPath(normalizeQueueId(bankId), normalizeQueueId(id)),
      query: {
        forceDraft: "true",
        ...query,
      },
    };
  }

  const legacyContentEditor = normalizeQueueId(contentEditor) === "legacy";
  return {
    path: getAdminContentEditUrl(
      normalizeQueueId(bankId),
      normalizeQueueId(id),
      legacyContentEditor
        ? {
            visualForVideoBank: false,
            visualForQuestionBanks: false,
            visualForInterviewBank: false,
          }
        : {},
    ),
    query,
  };
};

const getBrowserDocumentNavigate = () => {
  if (typeof window === "undefined") return null;

  try {
    return (href) => window.location.assign(href);
  } catch {
    return null;
  }
};

export const navigateAdminDraftQueuePreviewRoute = ({
  router,
  route,
  navigate = getBrowserDocumentNavigate(),
} = {}) => {
  if (!router || typeof router.resolve !== "function") return false;
  if (typeof navigate !== "function") return false;

  const href = normalizeQueueId(router.resolve(route)?.href);
  if (!href) return false;

  navigate(href);
  return true;
};

const getBrowserWindowOpen = () => {
  if (typeof window === "undefined") return null;
  return window.open.bind(window);
};

export const openAdminDraftQueuePreviewRoute = ({
  router,
  kind,
  bankId,
  id,
  queueKey = "",
  contentEditor = "default",
  bankHint = "",
  openWindow = getBrowserWindowOpen(),
} = {}) => {
  if (!router || typeof router.resolve !== "function") return false;
  if (typeof openWindow !== "function") return false;

  const route = buildAdminDraftQueueRoute({
    kind,
    bankId,
    id,
    queueKey,
    contentEditor,
    destination: ADMIN_DRAFT_QUEUE_DESTINATION_PREVIEW,
    bankHint,
  });
  const href = normalizeQueueId(router.resolve(route)?.href);
  if (!href) return false;

  openWindow(href, "_blank");
  return true;
};
